/**
 * Terrain rendering: a geo-clipmap centred on the rider.
 *
 * The important word is *clipmap*, not "stack of squares". Level 0 is a solid block
 * of fine grid around the bike; every level above it is a hollow RING whose hole is
 * exactly the extent of the level inside it. Nested solid squares — which is what
 * this file used to draw — overdraw at the centre, fight for depth, and leave the
 * world unrendered past the outermost square. You could see black through the
 * mountain. That is the bug this layout exists to make impossible: the levels tile
 * the plane exactly once, edge to edge, with no overlap and no hole.
 *
 * Two invariants do all the work, and both must hold or seams come back:
 *
 *   1. Every level's centre snaps to a multiple of SNAP_CELLS * its own spacing, and
 *      RING_RES is divisible by 4. That makes each level's outer boundary land exactly
 *      on the next level's grid lines, so the hole is an integer number of coarse
 *      cells and both levels sample the terrain at literally the same world points.
 *
 *   2. A level's outer boundary is geomorphed onto the coarse level's tessellation.
 *      Even-indexed boundary vertices already coincide with coarse vertices; odd ones
 *      are pulled to the midpoint of their two even neighbours, which is exactly where
 *      the coarse level's straight edge passes. T-junction cracks cannot open, because
 *      the two edges are the same line segment. The morph is faded in over a band of
 *      cells rather than applied only to the last row, so nothing pops as the ring
 *      slides.
 *
 * Rebuild cost is kept off the frame budget by sliding: when a ring snaps it moves by
 * a whole number of cells, so the previous samples are shifted with a memmove and only
 * the newly exposed strip is sampled from the terrain function.
 */

import * as THREE from 'three';
import { Terrain, Surface, makeGroundSample } from '../world/terrain';
import { Tuning } from '../core/tuning';

/**
 * Cells per side of every level's grid. Must be divisible by 4.
 *
 * 128 rather than 96 is bought specifically for the 2 km silhouette. Each level covers
 * RING_RES/2 cells of its own spacing, so widening the grid moves every distance band
 * one level finer: the ground 2 km out is now tessellated at 32 m instead of 64 m, and
 * a ridgeline sampled at 64 m is a ridgeline with its crest rounded off. It also buys a
 * level back — 8 levels of 128 reach further than 9 of 96 — so the extra vertices cost
 * roughly one ring, not a third of the terrain.
 */
const RING_RES = 128;
/**
 * Number of levels. Spacing doubles each level, so with lod0Spacing = 0.5 m the
 * outermost ring reaches 4.1 km — past the 3.2 km far plane in every direction,
 * including the diagonals. Anything less and the horizon shows through.
 */
const RING_LEVELS = 8;
/** Ring centres snap to this many cells. Must be even. */
const SNAP_CELLS = 2;
/** Width in cells of the band over which a level morphs onto the coarser level. */
const MORPH_BAND = 8;

const N = RING_RES + 1; // vertices per side
const VERTS = N * N;
const HALF_CELLS = RING_RES / 2;

interface Ring {
  level: number;
  spacing: number;
  mesh: THREE.Mesh;
  geo: THREE.BufferGeometry;
  pos: THREE.BufferAttribute;
  nrm: THREE.BufferAttribute;
  col: THREE.BufferAttribute;
  idx: THREE.BufferAttribute;
  /** Actual world centre of the data currently in the buffers. */
  cx: number;
  cz: number;
  hasData: boolean;
  /** Raw (unmorphed) terrain samples, indexed j*N+i. */
  h: Float64Array;
  nx: Float32Array;
  ny: Float32Array;
  nz: Float32Array;
  surf: Uint8Array;
  /** Hole in cell coordinates, [i0,i1) x [j0,j1). */
  hi0: number;
  hi1: number;
  hj0: number;
  hj1: number;
}

export class TerrainMesh {
  /** Named so a diagnostic can isolate the landform from everything scattered on it. */
  readonly group = new THREE.Group();
  private readonly rings: Ring[] = [];
  private readonly sample = makeGroundSample();
  /** Per-vertex morph weight; depends only on the grid index, so it is built once. */
  private readonly morphW = new Float32Array(VERTS);
  /** Scratch for the surface-colour prefilter. One ring composes at a time. */
  private readonly colA = new Float32Array(VERTS * 3);
  private readonly colB = new Float32Array(VERTS * 3);

  constructor(private readonly terrain: Terrain, private readonly material: THREE.Material) {
    this.group.name = 'terrain';
    const bandStart = (HALF_CELLS - MORPH_BAND) / HALF_CELLS;
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < N; i++) {
        const di = Math.abs(i - HALF_CELLS);
        const dj = Math.abs(j - HALF_CELLS);
        const d = Math.max(di, dj) / HALF_CELLS;
        const t = Math.min(1, Math.max(0, (d - bandStart) / (1 - bandStart)));
        this.morphW[j * N + i] = t * t * (3 - 2 * t);
      }
    }

    for (let level = 0; level < RING_LEVELS; level++) {
      const spacing = Tuning.world.lod0Spacing * Math.pow(2, level);
      const geo = new THREE.BufferGeometry();
      const pos = new THREE.BufferAttribute(new Float32Array(VERTS * 3), 3);
      const nrm = new THREE.BufferAttribute(new Float32Array(VERTS * 3), 3);
      const col = new THREE.BufferAttribute(new Float32Array(VERTS * 3), 3);
      pos.setUsage(THREE.DynamicDrawUsage);
      nrm.setUsage(THREE.DynamicDrawUsage);
      col.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute('position', pos);
      geo.setAttribute('normal', nrm);
      geo.setAttribute('color', col);
      // VERTS is 16641, so 16-bit indices are still enough for the whole grid.
      const idx = new THREE.BufferAttribute(new Uint16Array(RING_RES * RING_RES * 6), 1);
      idx.setUsage(THREE.DynamicDrawUsage);
      geo.setIndex(idx);
      geo.setDrawRange(0, 0);
      // Frustum culling is off (nine meshes, all of them usually visible), but a
      // bounding sphere still has to exist for anything that asks.
      geo.boundingSphere = new THREE.Sphere(
        new THREE.Vector3(0, 0, 0),
        Math.hypot(HALF_CELLS * spacing, HALF_CELLS * spacing) + 1200,
      );

      const mesh = new THREE.Mesh(geo, material);
      mesh.frustumCulled = false;
      mesh.castShadow = false;
      mesh.receiveShadow = true;
      // Fine levels first: front-to-back helps early-z, and the rings never overlap
      // so ordering can never change the image.
      mesh.renderOrder = level;
      mesh.matrixAutoUpdate = false;
      this.group.add(mesh);

      this.rings.push({
        level,
        spacing,
        mesh,
        geo,
        pos,
        nrm,
        col,
        idx,
        cx: 0,
        cz: 0,
        hasData: false,
        h: new Float64Array(VERTS),
        nx: new Float32Array(VERTS),
        ny: new Float32Array(VERTS),
        nz: new Float32Array(VERTS),
        surf: new Uint8Array(VERTS),
        hi0: 0,
        hi1: 0,
        hj0: 0,
        hj1: 0,
      });
    }
  }

  /** Metres from the centre to the outer edge of the whole clipmap. */
  get extent(): number {
    return HALF_CELLS * this.rings[this.rings.length - 1].spacing;
  }

  /**
   * Rebuild whatever has moved. Ring vertex work is budgeted across frames; the hole
   * geometry is not, because it is cheap and because it is what keeps the levels
   * watertight — it is always recomputed from where the rings *actually* are, so a
   * ring whose resample was deferred still stitches correctly this frame.
   */
  update(centreX: number, centreZ: number, budgetMs = 2.5): void {
    const t0 = performance.now();
    let built = 0;
    for (const ring of this.rings) {
      const step = ring.spacing * SNAP_CELLS;
      const tx = Math.round(centreX / step) * step;
      const tz = Math.round(centreZ / step) * step;
      if (ring.hasData && tx === ring.cx && tz === ring.cz) continue;
      // A ring with no data at all must be built now — a missing ring is a hole in
      // the world. Everything else yields once the budget is gone.
      if (ring.hasData && built > 0 && performance.now() - t0 > budgetMs) continue;
      this.move(ring, tx, tz);
      built++;
    }
    this.updateHole(this.rings[0], null);
    for (let l = 1; l < this.rings.length; l++) this.updateHole(this.rings[l], this.rings[l - 1]);
  }

  /** Slide a ring to a new snapped centre, sampling only the newly exposed strips. */
  private move(ring: Ring, tx: number, tz: number): void {
    if (!ring.hasData) {
      ring.cx = tx;
      ring.cz = tz;
      this.sampleRect(ring, 0, N, 0, N);
      ring.hasData = true;
    } else {
      const di = Math.round((tx - ring.cx) / ring.spacing);
      const dj = Math.round((tz - ring.cz) / ring.spacing);
      ring.cx = tx;
      ring.cz = tz;
      if (Math.abs(di) >= N || Math.abs(dj) >= N) {
        this.sampleRect(ring, 0, N, 0, N);
      } else {
        this.shift(ring, di, dj);
        // Newly exposed strips. Sampling the full column strip and then the full row
        // strip double-covers a small corner; that is cheaper than the bookkeeping to
        // avoid it.
        if (di > 0) this.sampleRect(ring, N - di, N, 0, N);
        else if (di < 0) this.sampleRect(ring, 0, -di, 0, N);
        if (dj > 0) this.sampleRect(ring, 0, N, N - dj, N);
        else if (dj < 0) this.sampleRect(ring, 0, N, 0, -dj);
      }
    }
    this.compose(ring);
    ring.mesh.position.set(ring.cx, 0, ring.cz);
    ring.mesh.updateMatrix();
  }

  /** memmove the retained samples so index (i,j) still means the same world point. */
  private shift(ring: Ring, di: number, dj: number): void {
    const arrs: (Float64Array | Float32Array | Uint8Array)[] = [ring.h, ring.nx, ring.ny, ring.nz, ring.surf];
    if (dj !== 0) {
      for (const a of arrs) {
        if (dj > 0) a.copyWithin(0, dj * N, VERTS);
        else a.copyWithin(-dj * N, 0, VERTS + dj * N);
      }
    }
    if (di !== 0) {
      for (const a of arrs) {
        for (let j = 0; j < N; j++) {
          const row = j * N;
          if (di > 0) a.copyWithin(row, row + di, row + N);
          else a.copyWithin(row - di, row, row + N + di);
        }
      }
    }
  }

  /** Sample the terrain into the raw arrays for a half-open index rect. */
  private sampleRect(ring: Ring, i0: number, i1: number, j0: number, j1: number): void {
    const s = ring.spacing;
    const half = HALF_CELLS * s;
    const originX = ring.cx - half;
    const originZ = ring.cz - half;
    for (let j = j0; j < j1; j++) {
      const wz = originZ + j * s;
      const row = j * N;
      for (let i = i0; i < i1; i++) {
        const g = this.terrain.sample(originX + i * s, wz, this.sample);
        const k = row + i;
        ring.h[k] = g.height;
        ring.nx[k] = g.nx;
        ring.ny[k] = g.ny;
        ring.nz[k] = g.nz;
        ring.surf[k] = g.surface;
      }
    }
  }

  /**
   * Surface class -> vertex colour, prefiltered with a separable 1-2-1 tent.
   *
   * The classifier is a hard decision per vertex. Left raw, a 32 m outer ring paints
   * whole triangles of one material and the hillside breaks into flat polygons of
   * grass and grey that read as a bug rather than as ground. One cell of blur turns
   * every boundary into a gradient the shading pass can interpolate through, and it
   * is the same prefiltering the coarse rings would need anyway to stop the
   * classification aliasing as the rings slide.
   */
  private filterColors(ring: Ring): Float32Array {
    const src = this.colA;
    const dst = this.colB;
    const surf = ring.surf;
    for (let k = 0; k < VERTS; k++) {
      const c = SURFACE_COLOR[surf[k]];
      const o = k * 3;
      src[o] = c[0];
      src[o + 1] = c[1];
      src[o + 2] = c[2];
    }
    // Horizontal pass src -> dst.
    for (let j = 0; j < N; j++) {
      const row = j * N;
      for (let i = 0; i < N; i++) {
        const k = row + i;
        const l = (i > 0 ? k - 1 : k) * 3;
        const r = (i < N - 1 ? k + 1 : k) * 3;
        const c = k * 3;
        dst[c] = (src[l] + src[c] * 2 + src[r]) * 0.25;
        dst[c + 1] = (src[l + 1] + src[c + 1] * 2 + src[r + 1]) * 0.25;
        dst[c + 2] = (src[l + 2] + src[c + 2] * 2 + src[r + 2]) * 0.25;
      }
    }
    // Vertical pass dst -> src, returning src.
    for (let j = 0; j < N; j++) {
      const row = j * N;
      const up = j > 0 ? row - N : row;
      const dn = j < N - 1 ? row + N : row;
      for (let i = 0; i < N; i++) {
        const c = (row + i) * 3;
        const u = (up + i) * 3;
        const d = (dn + i) * 3;
        src[c] = (dst[u] + dst[c] * 2 + dst[d]) * 0.25;
        src[c + 1] = (dst[u + 1] + dst[c + 1] * 2 + dst[d + 1]) * 0.25;
        src[c + 2] = (dst[u + 2] + dst[c + 2] * 2 + dst[d + 2]) * 0.25;
      }
    }
    return src;
  }

  /**
   * Raw samples -> vertex buffers, applying the geomorph toward the coarser level.
   *
   * The coarse level's surface, evaluated inside this grid, is the bilinear
   * interpolation of the even-indexed samples — because even indices are exactly the
   * coarse level's vertices (invariant 1). Blending the fine sample toward that with
   * a weight that reaches 1 on the outer boundary is what welds the two levels.
   */
  private compose(ring: Ring): void {
    const s = ring.spacing;
    const half = HALF_CELLS * s;
    const p = ring.pos.array as Float32Array;
    const nA = ring.nrm.array as Float32Array;
    const cA = ring.col.array as Float32Array;
    const h = ring.h;
    const rx = ring.nx;
    const ry = ring.ny;
    const rz = ring.nz;
    const morph = ring.level < RING_LEVELS - 1;
    const fc = this.filterColors(ring);

    for (let j = 0; j < N; j++) {
      const lz = -half + j * s;
      const row = j * N;
      const oddJ = (j & 1) === 1;
      for (let i = 0; i < N; i++) {
        const k = row + i;
        const o = k * 3;
        const w = morph ? this.morphW[k] : 0;

        let hh = h[k];
        let vnx = rx[k];
        let vny = ry[k];
        let vnz = rz[k];
        let cr = fc[o];
        let cg = fc[o + 1];
        let cb = fc[o + 2];

        if (w > 0) {
          const oddI = (i & 1) === 1;
          let a = k;
          let b = k;
          let n = 1;
          if (oddI && oddJ) {
            a = k - N - 1;
            b = k + N + 1;
            const c2 = k - N + 1;
            const d2 = k + N - 1;
            hh += ((h[a] + h[b] + h[c2] + h[d2]) * 0.25 - hh) * w;
            vnx += ((rx[a] + rx[b] + rx[c2] + rx[d2]) * 0.25 - vnx) * w;
            vny += ((ry[a] + ry[b] + ry[c2] + ry[d2]) * 0.25 - vny) * w;
            vnz += ((rz[a] + rz[b] + rz[c2] + rz[d2]) * 0.25 - vnz) * w;
            const ca = a * 3, cbi = b * 3, cc = c2 * 3, cd = d2 * 3;
            cr += ((fc[ca] + fc[cbi] + fc[cc] + fc[cd]) * 0.25 - cr) * w;
            cg += ((fc[ca + 1] + fc[cbi + 1] + fc[cc + 1] + fc[cd + 1]) * 0.25 - cg) * w;
            cb += ((fc[ca + 2] + fc[cbi + 2] + fc[cc + 2] + fc[cd + 2]) * 0.25 - cb) * w;
            n = 0;
          } else if (oddI) {
            a = k - 1;
            b = k + 1;
          } else if (oddJ) {
            a = k - N;
            b = k + N;
          } else {
            n = 0;
          }
          if (n === 1) {
            hh += ((h[a] + h[b]) * 0.5 - hh) * w;
            vnx += ((rx[a] + rx[b]) * 0.5 - vnx) * w;
            vny += ((ry[a] + ry[b]) * 0.5 - vny) * w;
            vnz += ((rz[a] + rz[b]) * 0.5 - vnz) * w;
            const ca = a * 3, cbi = b * 3;
            cr += ((fc[ca] + fc[cbi]) * 0.5 - cr) * w;
            cg += ((fc[ca + 1] + fc[cbi + 1]) * 0.5 - cg) * w;
            cb += ((fc[ca + 2] + fc[cbi + 2]) * 0.5 - cb) * w;
          }
        }

        p[o] = -half + i * s;
        p[o + 1] = hh;
        p[o + 2] = lz;
        const inv = 1 / Math.sqrt(vnx * vnx + vny * vny + vnz * vnz);
        nA[o] = vnx * inv;
        nA[o + 1] = vny * inv;
        nA[o + 2] = vnz * inv;
        cA[o] = cr;
        cA[o + 1] = cg;
        cA[o + 2] = cb;
      }
    }
    ring.pos.needsUpdate = true;
    ring.nrm.needsUpdate = true;
    ring.col.needsUpdate = true;
  }

  /**
   * Cut the hole for the level inside this one and re-emit the index buffer.
   * Derived from where both rings actually are right now, never from where they are
   * about to be, so a deferred resample cannot open a gap.
   */
  private updateHole(ring: Ring, inner: Ring | null): void {
    let i0 = 0;
    let i1 = 0;
    let j0 = 0;
    let j1 = 0;
    if (inner) {
      const s = ring.spacing;
      const originX = ring.cx - HALF_CELLS * s;
      const originZ = ring.cz - HALF_CELLS * s;
      const innerHalf = HALF_CELLS * inner.spacing;
      i0 = Math.round((inner.cx - innerHalf - originX) / s);
      i1 = Math.round((inner.cx + innerHalf - originX) / s);
      j0 = Math.round((inner.cz - innerHalf - originZ) / s);
      j1 = Math.round((inner.cz + innerHalf - originZ) / s);
      i0 = i0 < 0 ? 0 : i0 > RING_RES ? RING_RES : i0;
      i1 = i1 < 0 ? 0 : i1 > RING_RES ? RING_RES : i1;
      j0 = j0 < 0 ? 0 : j0 > RING_RES ? RING_RES : j0;
      j1 = j1 < 0 ? 0 : j1 > RING_RES ? RING_RES : j1;
      if (i1 <= i0 || j1 <= j0) i0 = i1 = j0 = j1 = 0;
    }
    if (i0 === ring.hi0 && i1 === ring.hi1 && j0 === ring.hj0 && j1 === ring.hj1 && ring.geo.drawRange.count > 0) {
      return;
    }
    ring.hi0 = i0;
    ring.hi1 = i1;
    ring.hj0 = j0;
    ring.hj1 = j1;

    const out = ring.idx.array as Uint16Array;
    let n = 0;
    for (let j = 0; j < RING_RES; j++) {
      const inJ = j >= j0 && j < j1;
      const row = j * N;
      for (let i = 0; i < RING_RES; i++) {
        if (inJ && i >= i0 && i < i1) continue;
        const v00 = row + i;
        const v10 = v00 + 1;
        const v01 = v00 + N;
        const v11 = v01 + 1;
        out[n] = v00; out[n + 1] = v01; out[n + 2] = v10;
        out[n + 3] = v10; out[n + 4] = v01; out[n + 5] = v11;
        n += 6;
      }
    }
    ring.idx.needsUpdate = true;
    ring.geo.setDrawRange(0, n);
  }
}

/** Base albedo per surface. Deliberately desaturated — saturation comes from lighting. */
const SURFACE_COLOR: Record<number, [number, number, number]> = {
  [Surface.Hardpack]: [0.38, 0.30, 0.22],
  [Surface.Loam]: [0.22, 0.16, 0.11],
  [Surface.Loose]: [0.46, 0.39, 0.30],
  [Surface.Rock]: [0.33, 0.32, 0.31],
  [Surface.Root]: [0.26, 0.20, 0.14],
  [Surface.Grass]: [0.24, 0.30, 0.16],
  [Surface.Snow]: [0.86, 0.89, 0.93],
};
